/**
 * Canonical OPERATIONAL enrollment resolver.
 *
 * One rule for every "how many students are registered?" surface in the admin
 * and on the public site: a registration counts toward a class only when it
 * resolves to that class's CURRENT or UPCOMING canonical session.
 *
 * Historical registrations (prior sessions/months) stay preserved and
 * searchable, but contribute 0 to operational counts. They are returned
 * separately as `historical` so a surface can show them under "Historical".
 *
 * This module does NOT invent counting rules — it composes the existing
 * canonical primitives:
 *   • listClassSessionOptions()  → which sessions are current/upcoming
 *   • rosterFor() / countsAsEnrolled() → who legitimately occupies a seat
 * and adds the one operational rule those primitives don't cover:
 * de-duplication by child + class + period.
 */

import {
  MANUAL_RESERVATIONS,
  MANUAL_SESSION_RESERVATIONS,
  countsAsEnrolled,
  countsTowardPaidMinimum,
  normalizeSessionLabel,
  rosterFor,
  type RegistrationChildRow,
  type RegistrationRow,
} from "@/lib/enrollment";
import { getClassAliases } from "@/lib/classIdentity";
import { listClassSessionOptions, type ResolveInput, type SessionOption } from "@/lib/nextClassSession";

export interface OperationalEnrollment {
  /** Canonical session the operational count refers to (null when none). */
  sessionLabel: string | null;
  /** Human label, e.g. "August Session (Aug 10 – Aug 31)". */
  sessionDisplay: string | null;
  /** "current" while running, "future" when it hasn't started yet. */
  sessionState: "current" | "future" | null;
  /**
   * True when the class has NO configured sessions at all — a variable /
   * ongoing operating model (e.g. a free club with rolling ride dates).
   * Such a class counts enrollment class-wide instead of per session.
   */
  sessionless: boolean;
  /** Distinct legitimate parent/family registration records (checkouts). */
  familyRegistrations: number;
  /** De-duplicated seats PHYSICALLY occupied for the current/upcoming session. */
  enrolled: number;
  /**
   * Subset of `enrolled` backed by real tuition — the only count the 5-student
   * minimum-enrollment policy (and the upcoming minimum-enrollment alerts) may
   * use. Comp / 100%-coupon / $0 seats occupy capacity but are excluded here.
   */
  paidEnrolled: number;
  /** Seats across every current/future session (multi-session classes). */
  upcomingEnrolledTotal: number;
  /** Per-session breakdown for current + future sessions. */
  perSession: Array<{ label: string; display: string; enrolled: number; paidEnrolled: number }>;
  /** Seats that belong to past sessions — preserved history, never operational. */
  historical: number;
}


/** Current + future sessions only, soonest first. Past/undated excluded. */
export function upcomingSessionOptions(input: ResolveInput, today: Date = new Date()): SessionOption[] {
  return listClassSessionOptions(input, today).filter(
    (s) => s.state === "current" || s.state === "future",
  );
}

function seatKey(classId: string, row: RegistrationRow, sessionLabel: string | null): string {
  const child = (row.child_name ?? "").trim().toLowerCase();
  const email = (row.email ?? "").trim().toLowerCase();
  const identity = child || `adult:${email}`;
  return `${classId}|${normalizeSessionLabel(sessionLabel) ?? "—"}|${identity}`;
}

/** Manual admin reservations for a class + session (alias-safe). */
function manualSeats(classId: string, sessionLabel: string | null): number {
  for (const alias of getClassAliases(classId)) {
    const n = sessionLabel
      ? MANUAL_SESSION_RESERVATIONS[`${alias}|${normalizeSessionLabel(sessionLabel)}`] ?? 0
      : MANUAL_RESERVATIONS[alias] ?? 0;
    if (n) return n;
  }
  return 0;
}

/**
 * De-duplicated seat count for one class + one canonical session.
 * Same rules as capacity: hidden / test / removed / fully-refunded / exception
 * rows contribute 0 (enforced inside rosterFor → countsAsEnrolled).
 */
export function operationalSeatsForSession(
  payments: RegistrationRow[],
  children: RegistrationChildRow[],
  classId: string,
  sessionLabel: string | null,
): number {
  const rows = rosterFor(payments, classId, sessionLabel, children);
  const seen = new Set<string>();
  for (const r of rows) seen.add(seatKey(classId, r, sessionLabel));
  return seen.size + manualSeats(classId, sessionLabel);
}

/**
 * PAID seats for one class + one canonical session — the minimum-enrollment
 * number. Same de-duplication and session scoping as
 * `operationalSeatsForSession`, but comp / 100%-coupon / $0 seats are dropped.
 * Manual admin reservations represent real off-platform paid seats and are
 * still included.
 */
export function operationalPaidSeatsForSession(
  payments: RegistrationRow[],
  children: RegistrationChildRow[],
  classId: string,
  sessionLabel: string | null,
): number {
  const rows = rosterFor(payments, classId, sessionLabel, children).filter(
    countsTowardPaidMinimum,
  );
  const seen = new Set<string>();
  for (const r of rows) seen.add(seatKey(classId, r, sessionLabel));
  return seen.size + manualSeats(classId, sessionLabel);
}

/**
 * Full operational picture for a class: what the card, roster header,
 * capacity readout and instructor views should all agree on.
 */
export function resolveOperationalEnrollment(
  payments: RegistrationRow[],
  children: RegistrationChildRow[],
  classId: string,
  input: ResolveInput,
  today: Date = new Date(),
): OperationalEnrollment {
  const all = listClassSessionOptions(input, today);
  const upcoming = all.filter((s) => s.state === "current" || s.state === "future");

  const aliases = new Set(getClassAliases(classId));

  // Distinct legitimate family registration records (parent checkouts).
  const familyRegistrations = payments.filter(
    (p) => aliases.has(String(p.class_id)) && countsAsEnrolled(p),
  ).length;

  // ---- Operating model: variable / ongoing (no configured sessions) --------
  // A class that has no sessions on file at all is not "between semesters" —
  // it simply doesn't use sessions (free clubs, rolling ride dates). Its
  // legitimate registrations are CURRENT enrollment, never history.
  if (all.length === 0) {
    const enrolled = operationalSeatsForSession(payments, children, classId, null);
    const paidEnrolled = operationalPaidSeatsForSession(payments, children, classId, null);
    return {
      sessionLabel: null,
      sessionDisplay: null,
      sessionState: null,
      sessionless: true,
      familyRegistrations,
      enrolled,
      paidEnrolled,
      upcomingEnrolledTotal: enrolled,
      perSession: [],
      historical: 0,
    };
  }

  const perSession = upcoming.map((s) => ({
    label: s.label,
    display: s.display,
    enrolled: operationalSeatsForSession(payments, children, classId, s.label),
    paidEnrolled: operationalPaidSeatsForSession(payments, children, classId, s.label),
  }));

  const primary = upcoming[0] ?? null;

  // Historical = legitimate seats that resolve to a past session, or to no
  // current/upcoming session at all. Counted once per child + class + period.
  const upcomingLabels = new Set(
    upcoming.map((s) => normalizeSessionLabel(s.label)).filter(Boolean) as string[],
  );
  const historicalSeats = new Set<string>();
  for (const p of payments) {
    if (!aliases.has(String(p.class_id))) continue;
    if (!countsAsEnrolled(p)) continue;
    const label =
      p.canonical_session_label ??
      (Array.isArray(p.selected_sessions) ? String(p.selected_sessions[0] ?? "") : null);
    const norm = normalizeSessionLabel(label ?? null);
    if (norm && upcomingLabels.has(norm)) continue;
    historicalSeats.add(seatKey(classId, p, norm));
  }

  return {
    sessionLabel: primary?.label ?? null,
    sessionDisplay: primary?.display ?? null,
    sessionState: primary ? (primary.state as "current" | "future") : null,
    sessionless: false,
    familyRegistrations,
    enrolled: primary ? perSession[0].enrolled : 0,
    paidEnrolled: primary ? perSession[0].paidEnrolled : 0,
    upcomingEnrolledTotal: perSession.reduce((s, x) => s + x.enrolled, 0),
    perSession,
    historical: historicalSeats.size,
  };

}
